import { type ButtonHTMLAttributes, type ReactNode, type Ref, useRef, useState } from 'react';

import { cn } from '@shared/lib/cn';
import { Icon } from '@shared/ui/icon';
import { Listbox, ListboxPopup, useListboxCombobox } from '@shared/ui/listbox';

import {
  SELECT_SIZE_CLASS,
  SELECT_STATE_CLASS,
  SELECT_VALUE_CLASS,
  type SelectOption,
  type SelectSize,
  type SelectStatus,
} from './select.constant';
import { SelectSkeleton } from './select.skeleton.ui';
import { SELECT_TEXT } from './select.text';

export interface SelectProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'defaultValue' | 'onChange' | 'children' | 'type' | 'role'
> {
  /** 선택지 */
  options: readonly SelectOption[];
  /** 선택된 값(제어). 없으면 `null` */
  value?: string | null;
  /** 비제어 초기값 */
  defaultValue?: string | null;
  /** 줄을 고르면 값과 그 줄을 넘긴다 */
  onChange?: (value: string, option: SelectOption) => void;
  /** 값이 없을 때의 글자(기본 "선택하세요") */
  placeholder?: string;
  /** Figma `size`: md 40 · sm 32(격자 표의 칸 안). 기본 md */
  size?: SelectSize;
  /** Figma `open`(제어). 비제어는 `defaultOpen` */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Figma `state=error` */
  invalid?: boolean;
  /**
   * Figma `status`. init = 트리거 크기의 스켈레톤, empty · loading = 열었을 때 목록의 모양.
   * 선택지가 비어 있으면 default 여도 목록이 empty 모양이다
   */
  status?: SelectStatus;
  /** 빈 목록 안내(기본 "항목이 없습니다") */
  emptyText?: ReactNode;
  /** 목록을 portal 대신 제자리(트리거 아래 문서 흐름)에 그린다(fe-rules 3절) */
  inline?: boolean;
  /** 폼 제출용 숨은 입력의 이름. 주면 `<input type="hidden">` 을 함께 그린다 */
  name?: string;
  /** 바깥 틀에 붙는다. 너비는 여기로: 기본은 부모 너비(Figma 240) */
  className?: string;
  /** 목록 상자에 붙는다 */
  listboxClassName?: string;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * 하나 고르기 선택 상자. 트리거는 TextInput 모양의 `role="combobox"` 버튼, 목록은 Listbox.
 * 키보드: 위 · 아래 · Home · End · Enter · Space · Esc. 도메인 선택 상자(FundSelect 등)가 이것을 감싸서 쓴다.
 */
export function Select({
  options,
  value: valueProp,
  defaultValue = null,
  onChange,
  placeholder = SELECT_TEXT.placeholder,
  size = 'md',
  open: openProp,
  defaultOpen,
  onOpenChange,
  invalid = false,
  status = 'default',
  emptyText,
  inline = false,
  name,
  disabled = false,
  className,
  listboxClassName,
  onKeyDown,
  onClick,
  ref,
  ...rest
}: SelectProps) {
  const [valueState, setValueState] = useState<string | null>(defaultValue);
  const value = valueProp !== undefined ? valueProp : valueState;
  const selected = options.find((option) => option.value === value);
  const filled = selected !== undefined;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const navigable = status !== 'empty' && status !== 'loading';
  const combobox = useListboxCombobox({
    options,
    selection: 'single',
    values: value === null ? [] : [value],
    onChoose: (option) => {
      if (valueProp === undefined) setValueState(option.value);
      onChange?.(option.value, option);
    },
    open: openProp,
    defaultOpen,
    onOpenChange,
    disabled: disabled || status === 'init',
    navigable,
    insideRefs: [wrapperRef, popupRef],
  });

  if (status === 'init') return <SelectSkeleton size={size} className={className} />;

  const stateClass = disabled
    ? SELECT_STATE_CLASS.disabled
    : invalid
      ? SELECT_STATE_CLASS.invalid
      : combobox.open
        ? SELECT_STATE_CLASS.open
        : SELECT_STATE_CLASS.default;
  const valueClass = disabled
    ? SELECT_VALUE_CLASS.disabled
    : filled
      ? SELECT_VALUE_CLASS.filled
      : SELECT_VALUE_CLASS.empty;

  return (
    <div
      ref={wrapperRef}
      data-slot="select"
      data-size={size}
      data-open={combobox.open}
      data-filled={filled}
      className={cn('flex w-full min-w-0 flex-col gap-1', className)}
    >
      <button
        ref={(node) => {
          triggerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={combobox.open}
        aria-controls={combobox.open ? combobox.listboxId : undefined}
        aria-activedescendant={combobox.activeDescendant}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        {...rest}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (!event.defaultPrevented) combobox.onKeyDown(event);
        }}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) combobox.onClick(event);
        }}
        className={cn(
          'flex w-full min-w-0 items-center gap-2 rounded-md text-left',
          SELECT_SIZE_CLASS[size],
          stateClass,
        )}
      >
        <span className={cn('min-w-0 flex-1 truncate', valueClass)}>
          {selected ? selected.label : placeholder}
        </span>
        {/* Figma 18×18 아이콘 자리 — 가장 가까운 토큰 md(20) */}
        <Icon
          name="chevron-down"
          size="md"
          className={disabled ? 'text-fg-disabled' : 'text-fg-muted'}
        />
      </button>
      {name ? <input type="hidden" name={name} value={value ?? ''} /> : null}
      {combobox.open ? (
        <ListboxPopup anchorRef={triggerRef} inline={inline} popupRef={popupRef}>
          <Listbox
            id={combobox.listboxId}
            options={options}
            selection="single"
            value={value === null ? [] : [value]}
            activeValue={combobox.active}
            onActiveValueChange={combobox.setActive}
            onChange={(_, option) => combobox.choose(option)}
            status={status}
            emptyText={emptyText}
            focusable={false}
            className={listboxClassName}
          />
        </ListboxPopup>
      ) : null}
    </div>
  );
}
